import type { AxiosRequestConfig } from "axios";
import axios from "./axios";
import { LocalStorageKey } from "../constants/localStorageKeys";

/**
 * Fetching image bytes (thumbnails, previews drawn on the map).
 *
 * Thumbnails live on the object store, on its own host. That host authenticates
 * by signed URL and rejects any request that also carries an `Authorization`
 * header — it answers 400 and the image never loads. The app's default axios
 * instance attaches that header to everything, so image requests need a client
 * that does not.
 *
 * `axios.create()` gives exactly that: the interceptors in utils/axios are
 * registered on the default instance only, so a created one never sees them.
 */
const bareAxios = axios.create();

/**
 * True when `url` points somewhere other than our own API — i.e. at a host that
 * never asked for our token. Thumbnails served by the API itself still need it,
 * so the header is dropped by destination rather than across the board.
 */
const isExternalUrl = (url: string): boolean =>
{
  try
  {
    const staticConfigs = JSON.parse(
      localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}",
    );
    const apiOrigin = staticConfigs?.REACT_APP_API_BASE_URL
      ? new URL(staticConfigs.REACT_APP_API_BASE_URL).origin
      : window.location.origin;

    return new URL(url, window.location.href).origin !== apiOrigin;
  } catch
  {
    return false;
  }
};

/**
 * GET an image as a blob, sending the auth header only to our own API.
 *
 * Note the two clients fail differently: the default instance resolves non-401
 * failures with the axios error (see the response interceptor), while the bare
 * one rejects. Callers must handle both — check the status *and* catch.
 */
export const fetchImageBlob = (url: string, config?: AxiosRequestConfig) =>
{
  const client = isExternalUrl(url) ? bareAxios : axios;

  return client.get(url, { responseType: "blob", ...config });
};
